import { useMemo } from 'react'
import { useInfiniteQuery } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { apiGet, urls } from 'api'
import { sparepartKeys, staleTime } from 'api/queryKeys'
import { SPAREPART_PAGE_LIMIT, SPAREPART_SORT } from 'constant'
import { logout } from 'store/actions'

const SPAREPART_LIST_URL = `${urls.service.prefix}/${urls.service.serviceManagement.sparepart.root}`
const SPAREPART_FETCH_ERROR = 'Gagal memuat data sparepart'
const SUCCESS_STATUS = 200

const toFiniteNumber = (value) => {
  const isNumeric = typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')
  if (!isNumeric) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const getPageRows = (page) => (Array.isArray(page?.data) ? page.data : [])

/**
 * react-query `getNextPageParam` for the paginated sparepart list.
 * Returns the next `offset` (rows loaded so far) or `undefined` when no page remains.
 * Uses `meta.total` when numeric; otherwise assumes more pages while the last page is full.
 * An empty last page always stops, so a stale `total` cannot cause an endless fetch loop.
 * @param {{ data?: Array, meta?: { limit?: number|string, total?: number|string } }} lastPage
 * @param {Array} allPages
 * @returns {number|undefined}
 */
export const getNextSparepartOffset = (lastPage, allPages = []) => {
  const lastPageSize = getPageRows(lastPage).length
  if (lastPageSize === 0) return undefined

  const loadedCount = allPages.reduce((sum, page) => sum + getPageRows(page).length, 0)
  const total = toFiniteNumber(lastPage?.meta?.total)
  if (total !== undefined) return total > loadedCount ? loadedCount : undefined

  const limit = toFiniteNumber(lastPage?.meta?.limit) ?? SPAREPART_PAGE_LIMIT
  return lastPageSize >= limit ? loadedCount : undefined
}

// apiGet never throws — reject on non-200 so react-query exposes `isError`.
const fetchSparepartPage = ({ token, filters, offset, signal, onLogout }) =>
  apiGet({
    token,
    url: SPAREPART_LIST_URL,
    data: {
      limit: SPAREPART_PAGE_LIMIT,
      offset,
      keywords: filters.keywords,
      sparepart_category_id: filters.categoryId,
      ...SPAREPART_SORT
    },
    signal,
    onLogout
  }).then(({ resData, resStatus }) => {
    if (resStatus === SUCCESS_STATUS) return resData
    const error = new Error(resData?.message || resData?.meta?.message || SPAREPART_FETCH_ERROR)
    error.response = { status: resStatus }
    throw error
  })

/**
 * Infinite (offset-paginated) sparepart list from `GET servis-service/admin/spareparts`,
 * filtered server-side by `keywords` and `categoryId`, sorted by id asc.
 * `keywords` must already be debounced by the caller (it is part of the query key).
 * @param {{ keywords?: string, categoryId?: number|string, isEnabled?: boolean }} [options]
 * @returns {{ spareparts: Array, isLoading: boolean, isError: boolean,
 *   isFetchingNextPage: boolean, canLoadMore: boolean, fetchNextPage: Function, refetch: Function }}
 */
export default function useInfiniteSpareparts({ keywords, categoryId, isEnabled = true } = {}) {
  const token = useSelector((state) => state.auth?.token)
  const dispatch = useDispatch()
  const filters = { keywords: keywords || undefined, categoryId: categoryId || undefined }

  const {
    data,
    isLoading,
    isError,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch
  } = useInfiniteQuery(
    sparepartKeys.infinite(filters),
    ({ pageParam = 0, signal }) =>
      fetchSparepartPage({
        token,
        filters,
        offset: pageParam,
        signal,
        onLogout: () => dispatch(logout())
      }),
    {
      enabled: isEnabled,
      getNextPageParam: getNextSparepartOffset,
      staleTime: staleTime.PAGED,
      refetchOnWindowFocus: false
    }
  )

  const spareparts = useMemo(() => (data?.pages ?? []).flatMap(getPageRows), [data?.pages])
  // `isFetching` also covers a background refetch of all pages — calling fetchNextPage then
  // would only join that refetch and stall loading, so wait until it settles.
  const canLoadMore = Boolean(hasNextPage) && !isFetching && !isError

  return {
    spareparts,
    isLoading,
    isError,
    isFetchingNextPage,
    canLoadMore,
    fetchNextPage,
    refetch
  }
}
